import { Dialog, Transition } from '@headlessui/react';
import useLocale from 'next-translate/useTranslation';
import { Fragment, useEffect } from 'react';

import clsxm from '@/lib/clsxm';

import SidebarPanel from './SidebarPanel';

/**
 * The menu below the desktop breakpoint: the sidebar in a sheet from the
 * start edge, opened by the tab bar's Menu or a layout's menu button. It sits
 * above the tab bar (z-30) and under toasts and tooltips.
 */
export default function MobileNavDrawer({
  open,
  onClose,
  className,
  closeFrom,
}: {
  open: boolean;
  onClose: () => void;
  /** Hides it where the layout's own sidebar takes over (`md:hidden`). */
  className?: string;
  /**
   * That breakpoint in px. The drawer closes when the window reaches it
   * (a phone turned sideways): hidden by CSS, the open dialog would still
   * keep the page inert and unscrollable.
   */
  closeFrom: number;
}) {
  const { t } = useLocale('dashboard');

  useEffect(() => {
    if (!open) return;
    const query = window.matchMedia(`(min-width: ${closeFrom}px)`);
    const close = () => query.matches && onClose();
    close();
    query.addEventListener('change', close);
    return () => query.removeEventListener('change', close);
  }, [open, closeFrom, onClose]);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as='div'
        className={clsxm('fixed inset-0 z-50 flex', className)}
        onClose={onClose}
      >
        <Transition.Child
          as={Fragment}
          enter='transition-opacity duration-200 ease-out'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='transition-opacity duration-150 ease-in'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div
            aria-hidden='true'
            className='fixed inset-0 bg-[#0f0f28]/40 dark:bg-black/60'
          />
        </Transition.Child>
        <Transition.Child
          as={Fragment}
          enter='transform transition duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]'
          enterFrom='-translate-x-full rtl:translate-x-full'
          enterTo='translate-x-0'
          leave='transform transition duration-200 ease-in'
          leaveFrom='translate-x-0'
          leaveTo='-translate-x-full rtl:translate-x-full'
        >
          <Dialog.Panel className='relative flex h-full w-[min(86vw,340px)] flex-col shadow-[0_0_48px_-12px_rgba(16,16,40,0.4)]'>
            <Dialog.Title className='sr-only'>{t('Menu')}</Dialog.Title>
            <SidebarPanel variant='drawer' onClose={onClose} />
          </Dialog.Panel>
        </Transition.Child>
      </Dialog>
    </Transition.Root>
  );
}
